import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getArtists, updateArtist, deleteArtist } from '../../services/adminService';

const inputStyle = {
  padding: '0.5rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
};

const PAGE_LIMIT = 20;

// When the artist has priority (creatorAboutFirst), their own name is what
// leads on the public site — mirror that here so the admin list matches.
const displayName = (artist) =>
  (artist.creatorAboutFirst && artist.creatorName) ? artist.creatorName : artist.name;

const AdminArtistList = () => {
  const { session } = useAuth();
  const navigate = useNavigate();

  const [artists, setArtists]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]             = useState(1);
  const [search, setSearch]         = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [featuredFilter, setFeaturedFilter]   = useState('');
  const [verifiedFilter, setVerifiedFilter]   = useState('');
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { setPage(1); }, [featuredFilter, verifiedFilter]);

  useEffect(() => {
    if (!session?.access_token) {
      setLoading(false);
      setError('No hay sesión activa.');
      return;
    }
    let cancelled = false;
    setLoading(true);

    const params = { page, limit: PAGE_LIMIT };
    if (debouncedSearch) params.search = debouncedSearch;
    if (featuredFilter === 'true') params.featured = 'true';
    if (verifiedFilter === 'true') params.verified = 'true';

    getArtists(session.access_token, params)
      .then((data) => {
        if (cancelled) return;
        setArtists(data.artists || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [session?.access_token, page, debouncedSearch, featuredFilter, verifiedFilter]);

  const handleToggle = async (artist, field) => {
    setTogglingId(`${artist.id}-${field}`);
    try {
      const updated = await updateArtist(session.access_token, artist.id, { [field]: !artist[field] });
      setArtists((prev) =>
        prev.map((a) => a.id === artist.id ? { ...a, [field]: updated.artist[field] } : a)
      );
    } catch (err) {
      alert(`Error al actualizar: ${err.message}`);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (artist) => {
    if (!window.confirm(`¿Eliminar a "${displayName(artist)}"? Esta acción no se puede deshacer.`)) return;
    setDeletingId(artist.id);
    try {
      await deleteArtist(session.access_token, artist.id);
      setArtists((prev) => prev.filter((a) => a.id !== artist.id));
      setTotal((t) => t - 1);
    } catch (err) {
      if (err.message?.includes('productos asociados')) {
        alert('No se puede eliminar: el artista tiene productos asociados. Reasigna o elimina los productos primero.');
      } else {
        alert(`Error al eliminar: ${err.message}`);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const ToggleBtn = ({ artist, field, onLabel, offLabel }) => {
    const key = `${artist.id}-${field}`;
    const isLoading = togglingId === key;
    const isOn = artist[field];
    return (
      <button
        onClick={() => handleToggle(artist, field)}
        disabled={isLoading}
        title={isOn ? `Quitar ${onLabel}` : `Marcar como ${onLabel}`}
        style={{
          background: 'none',
          border: 'none',
          color: isLoading ? '#ccc' : isOn ? '#28a745' : '#aaa',
          cursor: isLoading ? 'default' : 'pointer',
          fontSize: '0.82rem',
          fontWeight: '600',
          padding: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {isLoading ? '...' : isOn ? onLabel : offLabel}
      </button>
    );
  };

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Panel</Link>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', margin: 0 }}>
          Artistas
        </h1>
        <span style={{ color: '#888', fontSize: '0.9rem', marginLeft: 'auto' }}>
          {total} artista{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Filters + new button */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', margin: '1.5rem 0', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Buscar por nombre o slug..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, minWidth: '220px', flex: '1' }}
        />
        <select
          value={featuredFilter}
          onChange={(e) => setFeaturedFilter(e.target.value)}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          <option value="">Todos</option>
          <option value="true">Solo destacados</option>
        </select>
        <select
          value={verifiedFilter}
          onChange={(e) => setVerifiedFilter(e.target.value)}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          <option value="">Todos</option>
          <option value="true">Solo verificados</option>
        </select>
        <button
          onClick={() => navigate('/admin/artists/new')}
          style={{
            padding: '0.5rem 1.25rem',
            background: '#E4405F',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '0.9rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          + Nuevo artista
        </button>
      </div>

      {error && <p style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</p>}

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['Imagen', 'Nombre', 'Slug', 'Nacionalidad', 'Destacado', 'Verificado', ''].map((h) => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#666', fontWeight: '600', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>Cargando...</td>
                </tr>
              ) : artists.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>
                    No se encontraron artistas.
                  </td>
                </tr>
              ) : artists.map((artist) => (
                <tr key={artist.id} style={{ borderBottom: '1px solid #f8f8f8' }}>
                  <td style={{ padding: '0.6rem 1rem' }}>
                    {artist.profileImageUrl ? (
                      <img
                        src={artist.profileImageUrl}
                        alt={displayName(artist)}
                        style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: '50%', border: '1px solid #eee' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: '1.2rem' }}>
                        ✦
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: '#333' }}>
                    {displayName(artist)}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#888', fontSize: '0.85rem', fontFamily: 'monospace' }}>
                    {artist.slug || '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#555' }}>
                    {artist.nationality || '—'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <ToggleBtn artist={artist} field="featured" onLabel="Destacado" offLabel="No destacado" />
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <ToggleBtn artist={artist} field="verified" onLabel="Verificado" offLabel="No verificado" />
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      <Link
                        to={`/admin/artists/${artist.id}`}
                        style={{ color: '#2ca9e1', textDecoration: 'none', fontWeight: '500', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                      >
                        Editar →
                      </Link>
                      <button
                        onClick={() => handleDelete(artist)}
                        disabled={deletingId === artist.id}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: deletingId === artist.id ? '#ccc' : '#dc3545',
                          cursor: deletingId === artist.id ? 'default' : 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          padding: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {deletingId === artist.id ? 'Eliminando...' : 'Eliminar'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.5rem', justifyContent: 'center' }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px',
              background: page === 1 ? '#f8f9fa' : 'white',
              color: page === 1 ? '#aaa' : '#333',
              cursor: page === 1 ? 'default' : 'pointer', fontSize: '0.9rem',
            }}
          >
            ← Anterior
          </button>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>Página {page} de {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px',
              background: page === totalPages ? '#f8f9fa' : 'white',
              color: page === totalPages ? '#aaa' : '#333',
              cursor: page === totalPages ? 'default' : 'pointer', fontSize: '0.9rem',
            }}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminArtistList;
